import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useProposals } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import { ViewSwitcher } from "@/components/v2/view-switcher";
import { AggregateStrip } from "@/components/v2/aggregate-strip";
import { FilterToolbar } from "@/components/v2/filter-toolbar";
import { SearchHint } from "@/components/v2/search-hint";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { ProposalFormSheet } from "@/components/v2/proposal-form-sheet";
import { formatCurrency, formatDate } from "@/lib/utils";
import { LIST_PAGE_VIEWS } from "@/pages/v2/_shared/list-page-views";
import { formatShowingRange } from "@/pages/v2/_shared/format-showing-range";

type Row = Record<string, unknown>;

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "proposal_number",
    header: "Proposal #",
    cell: ({ row }) => {
      const number = row.original.proposal_number as string | undefined;
      return (
        <span className="typo-mono font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
          {number || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "customer_name",
    header: "Customer",
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.customer_name as string | undefined;
      return (
        <span className={name ? "" : "text-[color:var(--color-text-lo)]"}>
          {name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "final_price",
    header: "Total",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="font-medium">
        {row.original.final_price != null
          ? formatCurrency(row.original.final_price as number)
          : "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status as string} />,
  },
  {
    accessorKey: "assigned_rep_name",
    header: "Rep",
    cell: ({ row }) => {
      const rep = row.original.assigned_rep_name as string | undefined;
      return (
        <span className={rep ? "" : "text-[color:var(--color-text-lo)]"}>
          {rep || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: "Created",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.created_at as string)}
      </span>
    ),
  },
];

const VIEWS = LIST_PAGE_VIEWS.proposals;

export default function V2ProposalsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<string>(VIEWS.defaultValue);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const pageSize = 50;

  const { data, isLoading, isError, refetch } = useProposals({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const proposals = (data?.proposals ?? []) as Row[];
  const total = data?.total;

  // TODO(ADV-787-followup): Sent / Accepted / Conversion counts need a
  // DB-side aggregate view. Until then we render `—`.
  const aggregateItems = [
    {
      label: "Showing",
      value: formatShowingRange(proposals.length, pageIndex, pageSize, total),
    },
    { label: "Sent", value: "—" },
    { label: "Accepted", value: "—" },
    { label: "Conversion", value: "—" },
  ];

  return (
    <>
      <ListPage
        title="Proposals"
        actions={
          <>
            <SearchHint />
            <Button onClick={() => setSheetOpen(true)}>Create proposal</Button>
          </>
        }
        tabs={
          <ViewSwitcher
            value={view}
            onValueChange={setView}
            options={[...VIEWS.options]}
            aria-label="Proposals view"
          />
        }
        filters={<FilterToolbar entity="proposals" />}
        aggregateStrip={
          view === "table" ? (
            <AggregateStrip
              items={aggregateItems}
              sort={{ label: "Created", direction: "desc" }}
            />
          ) : undefined
        }
      >
        {view === "table" && (
          <DataTable
            columns={columns}
            data={proposals}
            total={total}
            isLoading={isLoading}
            isError={isError}
            errorEntityLabel="proposals"
            onRetry={() => refetch()}
            onRowClick={(row) => navigate(`/proposals/${row.id}`)}
            emptyMessage="No proposals yet"
            emptyDescription="Proposals appear here once you send one."
            pageSize={pageSize}
            pageIndex={pageIndex}
            onPaginationChange={setPageIndex}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        )}
        {view === "pipeline" && (
          <EmptyState
            title="Pipeline view ships in Wave 2"
            description="The Proposals pipeline lands in a follow-up ticket."
            action={{ label: "View as table", onClick: () => setView("table") }}
          />
        )}
        {view === "analytics" && (
          <EmptyState
            title="Analytics ships in Wave 2"
            description="The Proposals analytics surface is part of the broader operational-screens plan."
          />
        )}
      </ListPage>
      <ProposalFormSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onCreated={({ id }) => {
          toast.success("Proposal created");
          navigate(`/proposals/${id}`);
        }}
      />
    </>
  );
}
